import React, { useState, useRef, useEffect } from "react";
import { Page, Card, Layout, FormControl, Button } from "@fdoh/health-ui-react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import useClient from "../../hooks/useInspectionClient";
import type { SignUpInput } from "./SignUp.types";
import { FLORIDA_COUNTIES, formatPhoneNumber, formatFileSize, getFileExtension } from "./SignUp.constants";

/** Build FormData for sign-up or profile update (same shape as backend DTO). */
export function buildRegistryFormData(
    data: SignUpInput,
    selectedCounties: string[],
    actCertificates: File[]
): FormData {
    const formData = new FormData();
    const countiesValue = selectedCounties.length > 0 ? selectedCounties.join(", ") : data.countiesOfferingServices;
    const isPrivate = data.contactInformationPrivate === "Yes";

    formData.append("FirstName", data.firstName);
    formData.append("LastName", data.lastName);
    formData.append("Email", data.email);
    formData.append("Phone", data.phone);
    formData.append("StreetAddressLine1", data.address1);
    formData.append("City", data.city);
    formData.append("State", data.state);
    formData.append("PostalCode", data.postalCode);
    formData.append("LicenseType", data.licenseType);
    formData.append("CountiesOfferingServices", countiesValue);
    formData.append("ContactInformationPrivate", isPrivate.toString());
    formData.append("MiddleName", data.middleName || "");
    formData.append("Suffix", data.suffix || "");
    formData.append("BusinessName", data.businessName || "");
    formData.append("StreetAddressLine2", data.address2 || "");

    let qualificationNumber = "";
    if (data.licenseType === "Certified Environmental Health Professional" && data.cehpNumber) qualificationNumber = data.cehpNumber;
    else if ((data.licenseType === "Engineer who has completed the ACT" || data.licenseType === "Person supervised by an engineer") && data.engineerNumber) qualificationNumber = data.engineerNumber;
    else if (data.licenseType === "Master Septic Tank Contractor" && data.mstcNumber) qualificationNumber = data.mstcNumber;
    else if (data.licenseNumber) qualificationNumber = data.licenseNumber;
    formData.append("LicenseNumber", qualificationNumber);

    if (data.dateOfBirth?.trim()) formData.append("DateOfBirth", data.dateOfBirth);

    let qualificationExpiry = "";
    if (data.licenseType === "Certified Environmental Health Professional" && data.cehpExpirationDate) qualificationExpiry = data.cehpExpirationDate;
    else if ((data.licenseType === "Engineer who has completed the ACT" || data.licenseType === "Person supervised by an engineer") && data.engineerExpirationDate) qualificationExpiry = data.engineerExpirationDate;
    else if (data.licenseType === "Master Septic Tank Contractor" && data.mstcExpiration) qualificationExpiry = data.mstcExpiration;
    if (qualificationExpiry.trim()) formData.append("QualificationExpiryDate", qualificationExpiry);

    actCertificates.forEach(f => formData.append("Attachments", f, f.name));
    return formData;
}

const schema = yup.object({
    firstName: yup.string().required("First name is required"),
    middleName: yup.string().nullable(true).notRequired(),
    lastName: yup.string().required("Last name is required"),
    suffix: yup.string().nullable(true).notRequired(),
    email: yup.string().email("Enter a valid email").required("Email is required"),
    phone: yup.string()
        .required("Phone is required")
        .matches(/^\+1 \(\d{3}\) \d{3}-\d{4}$/, "Phone must be in format +1 (XXX) XXX-XXXX"),
    businessName: yup.string().nullable(true).notRequired(),
    address1: yup.string().required("Street address line 1 is required"),
    address2: yup.string().nullable(true).notRequired(),
    city: yup.string().required("City is required"),
    state: yup.string().required("State is required"),
    postalCode: yup.string().required("Postal code is required"),
    licenseType: yup.string().required("License type is required"),
    licenseNumber: yup.string().nullable(true).notRequired(),
    dateOfBirth: yup.string().nullable(true).notRequired(),
    countiesOfferingServices: yup.string().required("Counties offering services is required"),
    contactInformationPrivate: yup.string().required("Please select whether you wish to make your contact information private"),
    cehpNumber: yup.string().when("licenseType", {
        is: "Certified Environmental Health Professional",
        then: (schema) => schema.required("CEHP Number is required"),
        otherwise: (schema) => schema.nullable().notRequired()
    }),
    cehpExpirationDate: yup.string().when("licenseType", {
        is: "Certified Environmental Health Professional",
        then: (schema) => schema.required("CEHP Expiration Date is required"),
        otherwise: (schema) => schema.nullable().notRequired()
    }),
    engineerNumber: yup.string().nullable(true).notRequired(),
    engineerExpirationDate: yup.string().nullable(true).notRequired(),
    mstcNumber: yup.string().nullable(true).notRequired(),
    mstcExpiration: yup.string().nullable(true).notRequired()
});

export interface SignUpPageProps {
    /** When "profile", form is used to edit existing registration; submit uses onSubmitOverride and no duplicate check. */
    mode?: "signup" | "profile";
    /** Pre-fill form (used by Profile page). */
    initialData?: SignUpInput;
    /** When provided (e.g. in profile mode), called with form data instead of default sign-up submit. */
    onSubmitOverride?: (data: SignUpInput, formData: FormData) => Promise<void>;
    /** Optional note shown above the form (e.g. when prefilling from MSAL after no registration found). */
    signupPrefilledNote?: string;
}

const DEFAULT_SIGN_UP_VALUES: SignUpInput = {
    firstName: "",
    middleName: "",
    lastName: "",
    suffix: "",
    email: "",
    phone: "",
    businessName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    postalCode: "",
    licenseType: "",
    licenseNumber: "",
    dateOfBirth: "",
    countiesOfferingServices: "",
    contactInformationPrivate: "",
    cehpNumber: "",
    cehpExpirationDate: "",
    engineerNumber: "",
    engineerExpirationDate: "",
    mstcNumber: "",
    mstcExpiration: ""
};

export const SignUpPage = (props: SignUpPageProps = {}) => {
    const { mode = "signup", initialData, onSubmitOverride, signupPrefilledNote } = props;
    const { control, handleSubmit, reset, watch, setValue } = useForm<SignUpInput>({
        defaultValues: initialData ?? DEFAULT_SIGN_UP_VALUES,
        resolver: yupResolver(schema)
    });

    const client = useClient();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [actCertificates, setActCertificates] = useState<File[]>([]);
    const [selectedCounties, setSelectedCounties] = useState<string[]>([]);
    const [isCountyDropdownOpen, setIsCountyDropdownOpen] = useState(false);
    const countyDropdownRef = useRef<HTMLDivElement>(null);

    // Watch qualification type to show/hide conditional fields
    const selectedQualification = watch("licenseType");
    const prevQualification = useRef<string>("");

    // Clear conditional fields when qualification type changes
    useEffect(() => {
        if (prevQualification.current && prevQualification.current !== selectedQualification) {
            // Clear all conditional fields when qualification changes
            setValue("cehpNumber", "");
            setValue("cehpExpirationDate", "");
            setValue("engineerNumber", "");
            setValue("engineerExpirationDate", "");
            setValue("mstcNumber", "");
            setValue("mstcExpiration", "");
            setActCertificates([]);
        }
        prevQualification.current = selectedQualification || "";
    }, [selectedQualification, setValue]);

    // Sync form value with selectedCounties state when form value changes (only from external changes)
    const countiesValue = watch("countiesOfferingServices");
    const isUpdatingFromForm = useRef(false);

    // Pre-fill form when initialData is provided (Profile page).
    useEffect(() => {
        if (initialData) {
            reset(initialData);
            const counties = (initialData.countiesOfferingServices || "").split(", ").filter((c: string) => c.trim());
            setSelectedCounties(counties.length > 0 ? counties : []);
        }
    }, [initialData, reset]);

    useEffect(() => {
        if (!isUpdatingFromForm.current && countiesValue !== undefined) {
            if (countiesValue) {
                const counties = countiesValue.split(", ").filter((c: string) => c.trim());
                setSelectedCounties(counties.length > 0 ? counties : []);
            } else {
                setSelectedCounties([]);
            }
        }
        isUpdatingFromForm.current = false;
    }, [countiesValue]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (countyDropdownRef.current && !countyDropdownRef.current.contains(event.target as Node)) {
                setIsCountyDropdownOpen(false);
            }
        };

        if (isCountyDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isCountyDropdownOpen]);

    const handleCountyToggle = (county: string, onChange: (value: string) => void) => {
        isUpdatingFromForm.current = true;
        setSelectedCounties(prev => {
            const isSelected = prev.includes(county);
            const newSelection = isSelected
                ? prev.filter(c => c !== county)
                : [...prev, county];
            // Update form value
            onChange(newSelection.join(", "));
            return newSelection;
        });
    };

    const handleSelectAllCounties = (onChange: (value: string) => void) => {
        isUpdatingFromForm.current = true;
        const allSelected = selectedCounties.length === FLORIDA_COUNTIES.length;
        if (allSelected) {
            setSelectedCounties([]);
            onChange("");
        } else {
            setSelectedCounties([...FLORIDA_COUNTIES]);
            onChange(FLORIDA_COUNTIES.join(", "));
        }
    };
    const checkDuplicate = async (firstName: string, lastName: string, email: string): Promise<boolean> => {
        try {
            const queryParams = `firstName=${encodeURIComponent(firstName)}&lastName=${encodeURIComponent(lastName)}&email=${encodeURIComponent(email)}`;
            const response = await client.api("public/checkDuplicate").makeQuery(queryParams).get();
            return response.data?.isDuplicate === true;
        } catch (error) {
            console.error("Error checking duplicate:", error);
            // If duplicate check fails, allow submission to proceed (fail open)
            return false;
        }
    };

    const onSubmit = async (data: SignUpInput) => {
        setIsSubmitting(true);
        const formData = buildRegistryFormData(data, selectedCounties, actCertificates);
        try {
            if (onSubmitOverride) {
                await onSubmitOverride(data, formData);
                return;
            }

            // Check for duplicate email before submission (Email has UNIQUE constraint)
            const isDuplicate = await checkDuplicate(data.firstName, data.lastName, data.email);
            if (isDuplicate) {
                toast.error("A registration with this email address already exists. Please use a different email address or contact support if you believe this is an error.");
                setIsSubmitting(false);
                return;
            }

            const response = await client.api("public/signUpRequest").post(formData);

            if (response && response.data) {
                toast.success("Thank you. Your sign-up request has been submitted.");
                setIsSubmitted(true);
                reset();
                setSelectedCounties([]);
                setActCertificates([]);
            }
        } catch (err: any) {
            // Handle validation errors from backend
            if (err?.response?.data?.errors) {
                const errorMessages = Object.values(err.response.data.errors).flat().join(", ");
                toast.error(`Validation errors: ${errorMessages}`);
            } else if (err?.response?.data?.message) {
                toast.error(err.response.data.message);
            } else if (err?.message) {
                toast.error(err.message);
            } else {
                toast.error("Failed to submit. Please try again.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Page.Container>
            <Page.Body>
                <Layout.Container>
                    {!isSubmitted && (
                        <div className="row justify-content-center">
                            <div className="col col-md-6">
                                <h2 className="page-title">
                                    {mode === "profile" ? "Edit Your Registration" : "Private Provider Inspector Registration"}
                                </h2>
                                <p className="mt-2 mb-4">
                                    {mode === "profile"
                                        ? "Update your registration details below. The information on the registry will be publicly available."
                                        : "Use the following form to register as a private provider inspector for the Onsite Sewage Program."}
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="row justify-content-center">
                        <div className="col col-md-6">
                            <Card.Container>
                                <Card.Body>
                                    {isSubmitted ? (
                                        <div className="text-center py-5">
                                            <div className="mb-4">
                                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-success mx-auto">
                                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                                </svg>
                                            </div>
                                            <h3 className="text-success mb-3">Thank You!</h3>
                                            <p className="text-muted mb-4">
                                                Your registration request has been successfully submitted. We will review your information and contact you soon.
                                            </p>
                                            <p className="text-muted">
                                                For questions regarding your registration, please contact{" "}
                                                <a href="mailto:OSTDS_Feedback@FloridaDEP.gov">OSTDS_Feedback@FloridaDEP.gov</a> or{" "}
                                                <a href="tel:8502454070">850-245-4070</a>.
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                    {signupPrefilledNote && (
                                        <div className="alert alert-info mb-4" role="status">
                                            {signupPrefilledNote}
                                        </div>
                                    )}
                                    <div className="mb-4">
                                        <h4>Introduction</h4>
                                        <p className="text-muted">
                                            Private Providers of Inspections for OSTDS are required to register with the Department of Environmental Onsite Sewage Program. Please refer to our PPI webpage and Rule 62-6.024, Florida Administrative Code, to review the requirements to become a PPI, obtain necessary forms, and for other information.
                                        </p>
                                        <p className="text-muted">
                                            The information on this registry will be publicly available. Please ensure that all information provided is what you want clients to use to contact you.
                                        </p>
                                        <p className="text-muted">
                                            If you qualify as a PPI in more than one way specified by Rule 62-6.024, F.A.C., you only need to indicate one of them.
                                        </p>
                                        <p className="text-muted">
                                            For questions regarding filling out this form, please contact{" "}
                                            <a href="mailto:OSTDS_Feedback@FloridaDEP.gov">OSTDS_Feedback@FloridaDEP.gov</a> or{" "}
                                            <a href="tel:8502454070">850-245-4070</a>.
                                        </p>
                                    </div>
                                    <form>
                                        <Layout.Row>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <FormControl.Text control={control} name="firstName" label="First Name" required />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <FormControl.Text control={control} name="middleName" label="Middle Name" />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <FormControl.Text control={control} name="lastName" label="Last Name" required />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <FormControl.Text control={control} name="suffix" label="Suffix" />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <FormControl.Text control={control} name="email" label="Email" required />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <Controller
                                                        control={control}
                                                        name="phone"
                                                        render={({ field, fieldState }) => (
                                                            <div className="mb-3">
                                                                <label className="form-label">
                                                                    Phone <span className="text-danger">*</span>
                                                                </label>
                                                                <input
                                                                    type="tel"
                                                                    className={`form-control ${fieldState.error ? "is-invalid" : ""}`}
                                                                    placeholder="+1 (XXX) XXX-XXXX"
                                                                    value={field.value || ""}
                                                                    onChange={(e) => {
                                                                        const formatted = formatPhoneNumber(e.target.value);
                                                                        field.onChange(formatted);
                                                                    }}
                                                                    onBlur={field.onBlur}
                                                                    maxLength={18} // +1 (XXX) XXX-XXXX = 18 chars
                                                                />
                                                                {fieldState.error && (
                                                                    <div className="invalid-feedback">
                                                                        {fieldState.error.message}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <FormControl.Date control={control} name="dateOfBirth" label="Date of Birth" />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <FormControl.Text control={control} name="businessName" label="Business Name" />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <FormControl.Text control={control} name="address1" label="Address line 1" required />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <FormControl.Text control={control} name="address2" label="Address line 2" />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <FormControl.Text control={control} name="city" label="City" required />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <FormControl.Text control={control} name="state" label="State" required />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <FormControl.Text control={control} name="postalCode" label="Postal Code" required />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <Controller
                                                        control={control}
                                                        name="countiesOfferingServices"
                                                        render={({ field, fieldState }) => (
                                                            <div className="mb-3" ref={countyDropdownRef}>
                                                                <label className="form-label">
                                                                    Which Counties Would you Like Listed for Service <span className="text-danger">*</span>
                                                                </label>
                                                                <p className="text-muted small mb-2">
                                                                    Our website will include a search function for clients to search for PPIs. A PPI who qualifies may work in any county and this selection will not limit your ability to work across the state.
                                                                </p>
                                                                <div className="position-relative">
                                                                    <input
                                                                        type="text"
                                                                        className={`form-control ${fieldState.error ? "is-invalid" : ""}`}
                                                                        placeholder={selectedCounties.length > 0
                                                                            ? `${selectedCounties.length} county(ies) selected`
                                                                            : "Select counties"}
                                                                        readOnly
                                                                        onClick={() => setIsCountyDropdownOpen(!isCountyDropdownOpen)}
                                                                        value={field.value || selectedCounties.join(", ") || ""}
                                                                    />
                                                                    {isCountyDropdownOpen && (
                                                                        <div
                                                                            className="border border-danger rounded bg-white shadow-lg position-absolute w-100 mt-1"
                                                                            style={{
                                                                                maxHeight: "300px",
                                                                                overflowY: "auto",
                                                                                zIndex: 1000,
                                                                                top: "100%"
                                                                            }}
                                                                        >
                                                                            <div className="p-2 border-bottom">
                                                                                <label className="d-flex align-items-center mb-0" style={{ cursor: "pointer" }}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={selectedCounties.length === FLORIDA_COUNTIES.length && selectedCounties.length > 0}
                                                                                        onChange={() => handleSelectAllCounties(field.onChange)}
                                                                                        className="me-2"
                                                                                    />
                                                                                    <strong>Select All</strong>
                                                                                </label>
                                                                            </div>
                                                                            <hr className="my-1" />
                                                                            {FLORIDA_COUNTIES.map((county) => (
                                                                                <div key={county} className="p-2">
                                                                                    <label className="d-flex align-items-center mb-0" style={{ cursor: "pointer" }}>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={selectedCounties.includes(county)}
                                                                                            onChange={() => handleCountyToggle(county, field.onChange)}
                                                                                            className="me-2"
                                                                                        />
                                                                                        {county}
                                                                                    </label>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {fieldState.error && (
                                                                        <div className="invalid-feedback d-block">{fieldState.error.message}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <Controller
                                                        control={control}
                                                        name="contactInformationPrivate"
                                                        render={({ field, fieldState }) => (
                                                            <div className="mb-3">
                                                                <label className="form-label">
                                                                    Do you wish to make your contact information private? <span className="text-danger">*</span>
                                                                </label>
                                                                <p className="text-muted small mb-2">
                                                                    If <strong>yes</strong> is selected, Name, Business Name, Qualification Type, Qualification Number and Qualification Expiration will be listed. If <strong>no</strong> is selected in addition to the previously mentioned details, Email Address, Address and Phone Number will be listed.
                                                                </p>
                                                                <select
                                                                    className={`form-control ${fieldState.error ? "is-invalid" : ""}`}
                                                                    {...field}
                                                                    required
                                                                >
                                                                    <option value="">-- Select an option --</option>
                                                                    <option value="Yes">Yes</option>
                                                                    <option value="No">No</option>
                                                                </select>
                                                                {fieldState.error && (
                                                                    <div className="invalid-feedback d-block">{fieldState.error.message}</div>
                                                                )}
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col col-md-12">
                                                    <Controller
                                                        control={control}
                                                        name="licenseType"
                                                        render={({ field, fieldState }) => (
                                                            <div className="mb-3">
                                                                <label className="form-label">
                                                                    How do you qualify to be a private provider inspector <span className="text-danger">*</span>
                                                                </label>
                                                                <select
                                                                    className={`form-control ${fieldState.error ? "is-invalid" : ""}`}
                                                                    {...field}
                                                                    required
                                                                >
                                                                    <option value="">-- Select an option --</option>
                                                                    <option value="Certified Environmental Health Professional">Certified Environmental Health Professional</option>
                                                                    <option value="Engineer who has completed the ACT">Engineer who has completed the ACT</option>
                                                                    <option value="Person supervised by an engineer">Person supervised by an engineer</option>
                                                                    <option value="Master Septic Tank Contractor">Master Septic Tank Contractor</option>
                                                                </select>
                                                                {fieldState.error && (
                                                                    <div className="invalid-feedback d-block">{fieldState.error.message}</div>
                                                                )}
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            </div>

                                            {/* Conditional fields based on qualification type */}

                                            {/* Certified Environmental Health Professional */}
                                            {selectedQualification === "Certified Environmental Health Professional" && (
                                                <>
                                                    <div className="row">
                                                        <div className="col col-md-12">
                                                            <FormControl.Text control={control} name="cehpNumber" label="CEHP Number" required />
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col col-md-12">
                                                            <FormControl.Date control={control} name="cehpExpirationDate" label="CEHP Expiration Date" required />
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            {/* Engineer who has completed the ACT */}
                                            {selectedQualification === "Engineer who has completed the ACT" && (
                                                <>
                                                    <div className="row">
                                                        <div className="col col-md-12">
                                                            <FormControl.Text control={control} name="engineerNumber" label="Engineer Number" />
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col col-md-12">
                                                            <FormControl.Date control={control} name="engineerExpirationDate" label="Engineer Expiration Date" />
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col col-md-12">
                                                            <div className="mb-3">
                                                                <h5>Accelerated Certification Training Certificate</h5>
                                                                <p className="text-muted">Please upload your ACT certificate(s) below.</p>
                                                                <div className="border border-dashed rounded p-4 text-center" style={{ minHeight: "150px" }}>
                                                                    <div className="mb-3">
                                                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
                                                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                                            <polyline points="14 2 14 8 20 8"></polyline>
                                                                            <line x1="16" y1="13" x2="8" y2="13"></line>
                                                                            <line x1="16" y1="17" x2="8" y2="17"></line>
                                                                            <polyline points="10 9 9 9 8 9"></polyline>
                                                                        </svg>
                                                                    </div>
                                                                    <p className="mb-2">Drop your files here</p>
                                                                    <label className="btn btn-link text-primary" style={{ cursor: "pointer" }}>
                                                                        Browse
                                                                        <input
                                                                            type="file"
                                                                            className="d-none"
                                                                            multiple
                                                                            onChange={(e) => {
                                                                                const newFiles = Array.from(e.target.files || []);
                                                                                setActCertificates(prev => [...prev, ...newFiles]);
                                                                                // Reset the input so the same file can be selected again if needed
                                                                                e.target.value = '';
                                                                            }}
                                                                        />
                                                                    </label>
                                                                    {actCertificates.length > 0 && (
                                                                        <div className="mt-3">
                                                                            <h6 className="mb-2">File Upload</h6>
                                                                            <div className="list-group">
                                                                                {actCertificates.map((file, index) => {
                                                                                    const fileExt = getFileExtension(file.name);
                                                                                    const isPdf = fileExt === 'PDF';
                                                                                    return (
                                                                                        <div key={index} className="list-group-item d-flex align-items-start p-2 border-0 border-bottom">
                                                                                            <div className="me-2">
                                                                                                {isPdf ? (
                                                                                                    <div className="bg-primary text-white d-flex align-items-center justify-content-center rounded" style={{ width: "40px", height: "40px", fontSize: "10px", fontWeight: "bold" }}>
                                                                                                        PDF
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <div className="bg-secondary text-white d-flex align-items-center justify-content-center rounded" style={{ width: "40px", height: "40px", fontSize: "10px", fontWeight: "bold" }}>
                                                                                                        {fileExt}
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                            <div className="flex-grow-1 ms-2">
                                                                                                <div className="fw-normal" style={{ fontSize: "0.875rem", color: "#333" }}>
                                                                                                    {file.name.length > 50 ? `${file.name.substring(0, 50)}...` : file.name}
                                                                                                </div>
                                                                                                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                                                                                                    {formatFileSize(file.size)}
                                                                                                </div>
                                                                                            </div>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-sm btn-link text-danger p-0 ms-2"
                                                                                                onClick={() => {
                                                                                                    setActCertificates(prev => prev.filter((_, i) => i !== index));
                                                                                                }}
                                                                                                style={{ fontSize: "0.75rem", lineHeight: "1" }}
                                                                                            >
                                                                                                Remove
                                                                                            </button>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            {/* Person supervised by an engineer */}
                                            {selectedQualification === "Person supervised by an engineer" && (
                                                <>
                                                    <div className="row">
                                                        <div className="col col-md-12">
                                                            <FormControl.Text control={control} name="engineerNumber" label="Engineer Number" />
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col col-md-12">
                                                            <FormControl.Date control={control} name="engineerExpirationDate" label="Engineer Expiration Date" />
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col col-md-12">
                                                            <div className="mb-3">
                                                                <h5>Accelerated Certification Training Certificate and engineer's supervisory statement</h5>
                                                                <p className="text-muted">
                                                                    Please upload your ACT certificate(s) and at least one engineer's supervisory statement indicating they are supervising your OSTDS work below. The letter must be signed and dated per section 62-6.024(4)(a)8.
                                                                </p>
                                                                <div className="border border-dashed rounded p-4 text-center" style={{ minHeight: "150px" }}>
                                                                    <div className="mb-3">
                                                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
                                                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                                            <polyline points="14 2 14 8 20 8"></polyline>
                                                                            <line x1="16" y1="13" x2="8" y2="13"></line>
                                                                            <line x1="16" y1="17" x2="8" y2="17"></line>
                                                                            <polyline points="10 9 9 9 8 9"></polyline>
                                                                        </svg>
                                                                    </div>
                                                                    <p className="mb-2">Drop your files here</p>
                                                                    <label className="btn btn-link text-primary" style={{ cursor: "pointer" }}>
                                                                        Browse
                                                                        <input
                                                                            type="file"
                                                                            className="d-none"
                                                                            multiple
                                                                            onChange={(e) => {
                                                                                const newFiles = Array.from(e.target.files || []);
                                                                                setActCertificates(prev => [...prev, ...newFiles]);
                                                                                // Reset the input so the same file can be selected again if needed
                                                                                e.target.value = '';
                                                                            }}
                                                                        />
                                                                    </label>
                                                                    {actCertificates.length > 0 && (
                                                                        <div className="mt-3">
                                                                            <h6 className="mb-2">File Upload</h6>
                                                                            <div className="list-group">
                                                                                {actCertificates.map((file, index) => {
                                                                                    const fileExt = getFileExtension(file.name);
                                                                                    const isPdf = fileExt === 'PDF';
                                                                                    return (
                                                                                        <div key={index} className="list-group-item d-flex align-items-start p-2 border-0 border-bottom">
                                                                                            <div className="me-2">
                                                                                                {isPdf ? (
                                                                                                    <div className="bg-primary text-white d-flex align-items-center justify-content-center rounded" style={{ width: "40px", height: "40px", fontSize: "10px", fontWeight: "bold" }}>
                                                                                                        PDF
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <div className="bg-secondary text-white d-flex align-items-center justify-content-center rounded" style={{ width: "40px", height: "40px", fontSize: "10px", fontWeight: "bold" }}>
                                                                                                        {fileExt}
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                            <div className="flex-grow-1 ms-2">
                                                                                                <div className="fw-normal" style={{ fontSize: "0.875rem", color: "#333" }}>
                                                                                                    {file.name.length > 50 ? `${file.name.substring(0, 50)}...` : file.name}
                                                                                                </div>
                                                                                                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                                                                                                    {formatFileSize(file.size)}
                                                                                                </div>
                                                                                            </div>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-sm btn-link text-danger p-0 ms-2"
                                                                                                onClick={() => {
                                                                                                    setActCertificates(prev => prev.filter((_, i) => i !== index));
                                                                                                }}
                                                                                                style={{ fontSize: "0.75rem", lineHeight: "1" }}
                                                                                            >
                                                                                                Remove
                                                                                            </button>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            {/* Master Septic Tank Contractor */}
                                            {selectedQualification === "Master Septic Tank Contractor" && (
                                                <>
                                                    <div className="row">
                                                        <div className="col col-md-12">
                                                            <FormControl.Text control={control} name="mstcNumber" label="MSTC Number" />
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col col-md-12">
                                                            <FormControl.Date control={control} name="mstcExpiration" label="MSTC Expiration" />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </Layout.Row>
                                        <Card.Footer>
                                            <div className="d-flex">
                                                <div className="btn-list ms-auto">
                                                    <Button className="btn btn-primary" disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
                                                        {isSubmitting
                                                            ? (mode === "profile" ? "Saving..." : "Submitting...")
                                                            : (mode === "profile" ? "Save changes" : "Submit")}
                                                    </Button>
                                                </div>
                                            </div>
                                        </Card.Footer>
                                    </form>
                                    </>
                                    )}
                                </Card.Body>
                            </Card.Container>
                        </div>
                    </div>
                </Layout.Container>
            </Page.Body>
        </Page.Container>
    );
};

export default SignUpPage;


